/**
 * Mikrofon-Aufnahme + Spracherkennung (1:1 zur Web-App, chatbot.js).
 *  - getUserMedia + MediaRecorder (bevorzugtes MIME mit Fallbacks),
 *  - automatischer Stopp bei Sprechpause (AudioContext + RMS-Analyse),
 *  - Hard-Limit 60 s, Mindestaufnahme 1 s,
 *  - Ergebnis via /api/transcribe → Text-Callback (wird NICHT auto-gesendet).
 * Geht über die vorhandene Voice-I/O-Idee, aber funktional vollständig.
 */
import { signal } from "@preact/signals";
import { endpoints } from "../api/endpoints";
import { ApiError } from "../api/client";
import { getToken } from "../state/session";

export type RecorderState = "idle" | "recording" | "transcribing";

function preferredMimeType(): string {
  const MR = window.MediaRecorder;
  if (!MR || !MR.isTypeSupported) return "";
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  for (const t of types) if (MR.isTypeSupported(t)) return t;
  return "";
}

export interface RecorderHandlers {
  onText: (text: string) => void;
  onNotice: (text: string, quota?: boolean) => void;
}

export function createRecorder({ onText, onNotice }: RecorderHandlers) {
  const state = signal<RecorderState>("idle");

  let mediaRecorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let stream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let raf = 0;
  let speechSeen = false;
  let silenceStart = 0;
  let recStart = 0;

  function stopStream() {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
  }
  function stopSilenceWatcher() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    audioContext?.close().catch(() => {});
    audioContext = null;
    speechSeen = false;
    silenceStart = 0;
  }
  function stopRecording() {
    if (mediaRecorder && mediaRecorder.state === "recording") mediaRecorder.stop();
  }

  function startSilenceWatcher(s: MediaStream) {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    audioContext = new Ctx();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    audioContext.createMediaStreamSource(s).connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    recStart = performance.now();
    const voiceThreshold = 0.035;
    const silenceThreshold = 0.018;
    const silenceMs = 1300;
    const maxMs = 60000;

    const tick = () => {
      if (!mediaRecorder || mediaRecorder.state !== "recording") return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const n = (data[i] - 128) / 128;
        sum += n * n;
      }
      const rms = Math.sqrt(sum / data.length);
      const now = performance.now();
      if (rms > voiceThreshold) {
        speechSeen = true;
        silenceStart = 0;
      } else if (speechSeen && rms < silenceThreshold) {
        if (!silenceStart) silenceStart = now;
        if (now - silenceStart > silenceMs && now - recStart > 1000) {
          stopRecording();
          return;
        }
      } else {
        silenceStart = 0;
      }
      if (now - recStart > maxMs) {
        stopRecording();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }

  async function transcribe(blob: Blob) {
    state.value = "transcribing";
    try {
      const res = await endpoints.transcribe(blob);
      const text = String(res.text || "").trim();
      if (!text) {
        onNotice("Ich konnte in der Aufnahme keinen Text erkennen.");
        return;
      }
      onText(text);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        onNotice(err.detail || "Dein KI-Kontingent ist gerade aufgebraucht.", true);
      } else if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        /* Logout zentral im Client. */
      } else {
        onNotice(
          err instanceof ApiError
            ? err.detail
            : "Die Spracheingabe konnte nicht umgewandelt werden.",
        );
      }
    } finally {
      state.value = "idle";
    }
  }

  async function toggle() {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      stopRecording();
      return;
    }
    if (!getToken()) {
      onNotice("Bitte melde dich zuerst an, dann kann ich Spracheingaben verarbeiten.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      onNotice("Dein Browser unterstützt diese Spracheingabe leider nicht.");
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks = [];
      const mime = preferredMimeType();
      mediaRecorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      mediaRecorder.addEventListener("dataavailable", (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      });
      mediaRecorder.addEventListener("stop", () => {
        const type = mediaRecorder?.mimeType || mime || "audio/webm";
        stopSilenceWatcher();
        stopStream();
        state.value = "idle";
        if (!chunks.length) return;
        void transcribe(new Blob(chunks, { type }));
      });
      mediaRecorder.addEventListener("error", () => {
        stopSilenceWatcher();
        stopStream();
        state.value = "idle";
        onNotice("Die Aufnahme wurde abgebrochen.");
      });
      mediaRecorder.start();
      startSilenceWatcher(stream);
      state.value = "recording";
    } catch {
      stopSilenceWatcher();
      stopStream();
      state.value = "idle";
      onNotice("Ich konnte nicht auf dein Mikrofon zugreifen. Bitte prüfe die Browser-Freigabe.");
    }
  }

  function dispose() {
    stopRecording();
    stopSilenceWatcher();
    stopStream();
  }

  return { state, toggle, dispose };
}
